import { z } from 'zod';
import {
  ANALYST_BIO_MAX_LENGTH,
  FOLLOW_INVITE_DAYS,
  MEMBER_NAME_MAX_LENGTH,
  OPEN_FOLLOW_INVITES,
} from '../../config.ts';
import { counted, idSchema } from '../primitives.ts';
import { DAY_MS } from '../when.ts';
import { REVIEW_STATUSES, type ReviewStatus } from './review.ts';

export const ANALYST_STATUSES = REVIEW_STATUSES;
export type AnalystStatus = ReviewStatus;

export const FOLLOW_STATUSES = REVIEW_STATUSES;
export type FollowStatus = ReviewStatus;

export const analystIdSchema = idSchema;
export const followIdSchema = idSchema;

export const analystNameSchema = z.string().trim().min(1).max(MEMBER_NAME_MAX_LENGTH);
export const analystBioSchema = z.string().trim().min(1).max(ANALYST_BIO_MAX_LENGTH);

// Must match the case-insensitive unique index `analystNameMatches` reads.
export const analystNameKey = (name: string): string => name.trim().toLowerCase();

export const publishes = <T extends { status: AnalystStatus }>(
  analyst: T | null | undefined,
): analyst is T & { status: 'APPROVED' } => analyst?.status === 'APPROVED';

export function validateAnalystApply(existing: AnalystStatus | null): string[] {
  if (existing === 'PENDING') return ['your analyst application is already pending review'];
  if (existing === 'APPROVED') return ['you are already an analyst'];
  return [];
}

export function validateFollowRequest(who: {
  analyst: { memberId: number | null; status: AnalystStatus; listed: boolean } | undefined;
  memberId: number;
  existing: FollowStatus | null;
}): string[] {
  const { analyst, memberId, existing } = who;
  if (!analyst) return ['no such analyst'];
  if (analyst.memberId === memberId)
    return ['you are this analyst — your own recommendations are already yours'];
  if (!publishes(analyst)) return ['that analyst is not approved, so there is nothing to follow'];
  if (existing === 'APPROVED') return ['you already follow them'];
  // Unlisted, their answer is the invite they hand out; a waiting ask stays theirs to decide.
  if (!analyst.listed && existing !== 'PENDING') return [UNLISTED_FOLLOW];
  if (existing === 'PENDING') return ['your request to follow them is already pending'];
  return [];
}

export const UNLISTED_FOLLOW = 'that analyst takes followers only on an invite of their own';

export const followInviteIdSchema = idSchema;

export const NO_SUCH_FOLLOW_INVITE =
  'that invite link is not one of ours — check it was copied whole';

/** 32 hex characters: a link that lets a stranger in must not be guessable. */
export const followInviteCodeSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[0-9a-f]{32}$/, 'is not a follow invite');

/** When an invite stops letting anybody in, used or not. */
export const inviteExpiresAt = (invite: { createdAt: Date }): Date =>
  new Date(invite.createdAt.getTime() + FOLLOW_INVITE_DAYS * DAY_MS);

/** An invite made after this has not expired: `inviteExpiresAt` turned round, for SQL. */
export const inviteFreshSince = (now: Date): Date =>
  new Date(now.getTime() - FOLLOW_INVITE_DAYS * DAY_MS);

/** `email` is whose it is: null on one made before invites named somebody. */
type InviteState = {
  email: string | null;
  usedAt: unknown;
  revokedAt: unknown;
  createdAt: Date;
};

/** Open: naming somebody, and neither used, withdrawn nor expired, so it still lets them in. */
export const inviteOpen = (invite: InviteState, now: Date): boolean =>
  invite.email !== null &&
  invite.usedAt === null &&
  invite.revokedAt === null &&
  invite.createdAt.getTime() > inviteFreshSince(now).getTime();

/**
 * Why none of an analyst's invites lets anybody in, or null while they may. Their yes is worth
 * what their own standing is: an owner the admins stood down lets nobody in.
 */
export function invitesShut(
  analyst: { name: string; status: AnalystStatus },
  owner: { status: ReviewStatus } | null,
): string | null {
  if (!publishes(analyst)) return `${analyst.name} is not approved, so nobody can follow them`;
  if (owner !== null && !ownerDecides(owner)) {
    return `${analyst.name}’s invites let nobody in while their own access is withdrawn`;
  }
  return null;
}

export function validateInviteMint(
  analyst: { name: string; status: AnalystStatus },
  owner: { status: ReviewStatus } | null,
  open: number,
): string[] {
  const shut = invitesShut(analyst, owner);
  if (shut !== null) return [shut];
  if (open >= OPEN_FOLLOW_INVITES) {
    return [
      `${analyst.name} already has ${OPEN_FOLLOW_INVITES} invites nobody has used — withdraw some first`,
    ];
  }
  return [];
}

/**
 * Whether one person may come in on an invite and follow. An invite is the analyst's yes and lets
 * a stranger in, but never past an admin's no.
 */
export function validateInviteJoin(who: {
  invite: InviteState;
  /** The address Google signed them in as. */
  email: string;
  analyst: { name: string; memberId: number | null; status: AnalystStatus };
  /** The member who claims the analyst, null for an unclaimed one. */
  owner: { status: ReviewStatus } | null;
  member: { id: number; status: ReviewStatus } | null;
  follow: FollowStatus | null;
  now: Date;
}): string[] {
  const { invite, email, analyst, owner, member, follow, now } = who;
  if (invite.revokedAt !== null) return [`${analyst.name} has withdrawn that invite`];
  if (invite.usedAt !== null) return ['that invite has already been used'];
  if (invite.email === null) {
    return [`that invite names nobody, so it lets nobody in — ask ${analyst.name} for a new one`];
  }
  if (!inviteOpen(invite, now)) {
    return [
      `that invite expired ${FOLLOW_INVITE_DAYS} days after it was made — ask ${analyst.name} for a new one`,
    ];
  }
  if (invite.email !== email) {
    return [`that invite is for ${invite.email} — sign in with Google as that address to take it`];
  }
  const shut = invitesShut(analyst, owner);
  if (shut !== null) return [shut];
  if (member?.status === 'REJECTED') {
    return ['the admins have turned this address away, and an invite cannot overrule them'];
  }
  if (member !== null && analyst.memberId === member.id) {
    return ['you are this analyst — your own recommendations are already yours'];
  }
  if (follow === 'APPROVED') return [`you already follow ${analyst.name}`];
  return [];
}

/**
 * An analyst's owner decides its asks (drafts, follow requests, rolls) while an approved member,
 * whatever the listing's own standing; with none, the desk decides. `decidingOwner` in
 * `queries/fragments.ts` is this in SQL — change both.
 */
export const ownerDecides = (owner: { status: ReviewStatus }): boolean =>
  owner.status === 'APPROVED';

/** Who decides for an analyst whose deciding owner is `owner`: them, or with none the desk. */
export const decidersFor = (
  owner: string | null,
  adminEmails: readonly string[],
): readonly string[] => (owner === null ? adminEmails : [owner]);

/** Whether a viewer decides for an analyst, given its deciding owner's id, null for none. */
export const decidesFor = (
  viewer: { desk: boolean; analystId: number | null },
  analyst: { id: number; ownerId: number | null },
): boolean => (viewer.desk ? analyst.ownerId === null : analyst.id === viewer.analystId);

export function confirmsAnalystName(analyst: { name: string }, typed: string): boolean {
  return analystNameKey(typed) === analystNameKey(analyst.name);
}

export function validateAnalystRemoval(
  analyst: { name: string },
  standing: { followers: number; recommendations: number },
): string[] {
  const violations: string[] = [];
  if (standing.followers > 0) {
    violations.push(
      `${analyst.name} has ${counted(standing.followers, 'follower', 'followers')} — ` +
        'a name people follow is stood down by rejecting it, not deleted out from under them',
    );
  }
  if (standing.recommendations > 0) {
    violations.push(
      `${standing.recommendations} recommendation${standing.recommendations === 1 ? ' stands' : 's stand'} ` +
        `filed under ${analyst.name} — their record outlives the listing. Remove the ` +
        'recommendations first, or reject them instead',
    );
  }
  return violations;
}
